import { FC, MouseEvent, useEffect, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import { useController, useFormContext } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import Text from "@/components/Text";
import { Textarea } from "@/components/Forms";
import {
  HighlightResource,
  ProductionSceneActorResource,
  ProductionSceneLocationResource,
  ProductionSceneNoteResource,
  ProductionScenePropResource,
  ProductionSceneResource,
  ProductionSceneShoppingItemResource,
} from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { SceneFormT } from "./ProductionScriptBlock";
import SidebarLocations from "./SidebarLocations";
import SidebarActors from "./SidebarActors";
import SidebarProps from "./SidebarProps";
import SidebarNotes from "./SidebarNotes";
import { useParams } from "react-router-dom";
import { scrollBarStyles } from "@/utils/theme";

const Box = styled.div`
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const SceneHeading = styled.div`
  display: flex;
  margin-bottom: 52px;
  flex-wrap: wrap;
`;

const BoxBottom = styled.div`
  margin-top: 30px;
`;

const SceneNumber = styled(Text)`
  background: ${({ theme }) => theme.colors.lightGray};
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 12px;
  transform: translateY(8px);
`;

export const PlusButton = styled.span`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  width: 16px;
  height: 16px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transform: translateY(-1px);
  color: ${({ theme }) => theme.colors.purpleLighter};
  transition: background-colo ease-out 0.2s;
`;

export const InfoHeadline = styled(Text)`
  margin-bottom: 16px;
  margin-top: 40px;
  display: flex;
  align-items: center;
  &:first-child {
    margin-top: 0;
  }
  &:hover {
    ${PlusButton} {
      background-color: ${({ theme }) => theme.colors.grayscale};
    }
  }
`;

export const InfoBlock = styled.div`
  display: flex;
  margin-bottom: 16px;
  svg {
    margin-right: 10px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.gray};
    transform: translateY(-1px);
  }
`;

const Highlights = styled.div<{ $isActive: boolean }>`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 2px;
  height: 40px;
  display: flex;
  padding: 0 10px;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  ${({ $isActive }) =>
    $isActive &&
    css`
      background: rgba(159, 154, 230, 0.08);
    `}
`;

const StyledValidationMessage = styled(ValidationMessage)`
  width: 100%;
`;

const HighlightsHeading = styled(Text)`
  margin-bottom: 10px;
`;

const DeleteScene = styled(Text)`
  padding-bottom: 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray};
  display: inline-block;
  margin-top: 40px;
  cursor: pointer;
`;

const CategoriesWrapper = styled.div`
  max-height: 750px;
  overflow: auto;
  padding-right: 8px;
  ${scrollBarStyles};
`;

const StyledTextarea = styled(Textarea)`
  padding: 4px 8px;
  font-size: 16px;
  line-height: 24px;
  height: 34px;
  min-height: 34px;
  border-color: transparent;
  border-radius: 2px;
  font-weight: 700;
  resize: none;
  overflow: hidden;
  flex: 1;
  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colors.grayscale};
  }
`;

const HighlightsError = styled(ValidationMessage)`
  border-radius: 2px;
  padding: 12px;
  svg {
    margin-right: 8px;
  }
  > span {
    font-size: 9px;
    line-height: 12px;
    color: ${({ theme }) => theme.colors.error};
  }
`;

interface Props {
  scene: ProductionSceneResource;
  sceneNumber: number;
  sidebarData:
    | {
        shoppingItems: ProductionSceneShoppingItemResource[][];
        props: ProductionScenePropResource[][];
        locations: ProductionSceneLocationResource[][];
        actors: ProductionSceneActorResource[][];
        notes: ProductionSceneNoteResource[][];
        scenes: number[];
      }
    | null
    | undefined;
  setFirstSceneAsActive: () => void;
}

const ProductionScriptBlockInfo: FC<Props> = ({
  scene,
  sceneNumber,
  sidebarData,
  setFirstSceneAsActive,
}) => {
  const { teamId } = useParams();

  const { data: mandatoryHighlights } = useQuery(
    ["script_highlights", { teamId }],
    () =>
      api.get<never, HighlightResource[]>("/v1/production/highlights", {
        params: {
          "filter[team_id]": teamId,
        },
      }),
    {
      select: (highlights) =>
        highlights.filter((h) => h.isMandatory).map((h) => h.id),
    }
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const textAreaAdjust = () => {
    const element = textareaRef.current;

    element!.style.height = "1px";
    element!.style.height = 2 + element!.scrollHeight + "px";
  };

  const totalScore = useMemo(() => {
    return scene.highlights?.reduce((acc, curr) => acc + curr.value, 0) ?? 0;
  }, [scene]);

  const hasAllMandatoryHighlights = useMemo(() => {
    if (!mandatoryHighlights) return true;

    const highlightsIds = scene.highlights!.map((h) => h.id);

    return mandatoryHighlights.every((h) => highlightsIds.includes(h));
  }, [scene.highlights, mandatoryHighlights]);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this scene?",
        scene.name
      );

      if (!confirmed) return;

      await api.delete(`/v1/production/scenes/${scene.id}`);

      await queryClient.invalidateQueries(["production_projects"]);

      setFirstSceneAsActive();

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const { control } = useFormContext<SceneFormT>();

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name: "name",
    control,
  });

  useEffect(() => {
    textAreaAdjust();
  }, [value]);

  return (
    <Box>
      <div>
        <SceneHeading>
          <SceneNumber $type="extraSmall">{sceneNumber}</SceneNumber>
          <StyledTextarea
            ref={textareaRef}
            $hasError={!!error}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            value={value}
            placeholder="Add scene name"
          />
          {!!error && (
            <StyledValidationMessage>{error.message}</StyledValidationMessage>
          )}
        </SceneHeading>
        <CategoriesWrapper>
          <SidebarLocations
            sceneNumber={sceneNumber}
            scene={scene}
            locations={sidebarData?.locations}
          />
          <SidebarActors
            sceneNumber={sceneNumber}
            scene={scene}
            actors={sidebarData?.actors}
          />
          <SidebarProps
            sceneNumber={sceneNumber}
            scene={scene}
            props={sidebarData?.props}
          />
          <SidebarNotes
            sceneNumber={sceneNumber}
            scene={scene}
            notes={sidebarData?.notes}
          />
        </CategoriesWrapper>
      </div>
      <BoxBottom>
        <HighlightsHeading $type="smallHeadline" $color="gray">
          SCENE HIGHLIGHTS:
        </HighlightsHeading>
        <Highlights $isActive={!!totalScore}>
          <Text $type="smallHeadline" $color={!!totalScore ? "purple" : "gray"}>
            Total points: {totalScore}
          </Text>
        </Highlights>
        {!hasAllMandatoryHighlights && (
          <HighlightsError>
            Mandatory highlights are not marked in this scene.
          </HighlightsError>
        )}
        {sceneNumber !== 1 && (
          <DeleteScene
            as="button"
            $type="smallNormal"
            $color="gray"
            onClick={onDelete}
          >
            Delete scene
          </DeleteScene>
        )}
      </BoxBottom>
    </Box>
  );
};

export default ProductionScriptBlockInfo;
